import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { AuthService } from '../services/auth'
import { useAdminSelectedSchool } from '../utils/adminSchoolContext'
import { HomeworkService, type HomeworkSubmission, type Homework, type HomeworkSelection } from '../services/homework'
import { GroupService } from '../services/groups'
import CurrentHomeworkSection from '../components/CurrentHomeworkSection'
import submittedIconMark from '../assets/icon/submitted-icon-mark.svg'

// ─── Page ──────────────────────────────────────────────────────────────────────

const HomeworkPage = () => {
  const navigate = useNavigate()
  const adminSelectedSchool = useAdminSelectedSchool()
  const [submissions, setSubmissions] = useState<HomeworkSubmission[]>([])
  const [schoolHomeworks, setSchoolHomeworks] = useState<Homework[]>([])
  const [systemSelection, setSystemSelection] = useState<HomeworkSelection | null>(null)
  const [entreHomeworks, setEntreHomeworks] = useState<Homework[]>([])
  const [isInEntreClub, setIsInEntreClub] = useState(false)
  const [isAdmin, setIsAdmin] = useState(false)
  const [totalSystemCount, setTotalSystemCount] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    const init = async () => {
      const user = await AuthService.getCurrentUser()
      if (!user) { navigate('/login'); return }

      const isAdminUser = user.role === 'admin' || user.role === 'super_admin'
      setIsAdmin(isAdminUser)

      try {
        const effectiveSchoolId = isAdminUser ? (adminSelectedSchool?.id ?? null) : user.school_id

        // One parallel round: the active school/entre homework reuse the submissions
        // loaded here, and the entre homework is only shown to club members (or admins).
        const subsPromise = HomeworkService.getMySubmissions(user.id)
        const submittedHomeworkIds = subsPromise.then(subs => new Set(subs.map(s => s.homework_id)))
        submittedHomeworkIds.catch(() => {}) // handled through subsPromise below
        const [subs, selection, studentGroups, systemHomeworks, active, entre] = await Promise.all([
          subsPromise,
          HomeworkService.getSystemHomeworkSelection(user.id),
          isAdminUser ? Promise.resolve([]) : GroupService.getStudentGroups(user.id),
          HomeworkService.getSystemHomework(),
          effectiveSchoolId
            ? HomeworkService.getActiveSchoolHomeworks({
                studentId: user.id,
                schoolId: effectiveSchoolId,
                grade: user.grade,
                className: user.class,
                submittedHomeworkIds,
              })
            : Promise.resolve([] as Homework[]),
          effectiveSchoolId
            ? HomeworkService.getActiveEntreHomeworks({ studentId: user.id, schoolId: effectiveSchoolId, submittedHomeworkIds })
            : Promise.resolve([] as Homework[]),
        ])
        setSubmissions(subs)
        setSystemSelection(selection)
        setTotalSystemCount(systemHomeworks.length)

        const inEntreClub = studentGroups.some(g => g.is_entre_club)
        setIsInEntreClub(inEntreClub)
        setSchoolHomeworks(active)
        setEntreHomeworks(inEntreClub || isAdminUser ? entre : [])
      } catch (err) {
        console.error(err)
        setError('宿題の読み込みに失敗しました')
      } finally {
        setIsLoading(false)
      }
    }
    init()
  }, [navigate, adminSelectedSchool])

  const getTypeColor = (type: string) => {
    if (type === 'school') return '#da4574'
    if (type === 'entre') return '#ebad47'
    return '#65c1c8'
  }

  const isFullyGraded = (sub: HomeworkSubmission) => {
    if (sub.status !== 'graded') return false
    if (['school', 'entre'].includes(sub.homework?.type ?? '')) return sub.teacher_reviewed
    return true
  }

  const selectedHomework = systemSelection?.homework

  const formatDeadline = (deadline: string | null | undefined) => {
    if (!deadline) return 'なし'
    const d = new Date(deadline)
    return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日`
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-24">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#65c1c8]" />
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 pt-4 pb-8">
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}

      {/* ── Current homework section ── */}
      <div className="mb-6">
        <CurrentHomeworkSection
          schoolHomeworks={schoolHomeworks}
          entreHomeworks={entreHomeworks}
          showEntreCard={isInEntreClub || isAdmin}
          systemSelection={systemSelection}
          submissions={submissions}
          headerClassName="flex items-center gap-2 pb-3"
          cardRowClassName="flex gap-3 overflow-x-auto pb-1 scrollbar-hide"
        />
      </div>

      {/* ── Submission list + stats card ── */}
      <div className="bg-white rounded-2xl shadow-lg px-4 py-5 sm:px-6 sm:py-6 flex flex-col lg:flex-row gap-6 lg:items-start mb-8">

        {/* Left: unified submission list */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-4">
            <img alt="" className="h-10 w-[45px] shrink-0" src={submittedIconMark} />
            <span className="text-xl font-bold text-[#33333b]">提出済みの宿題</span>
          </div>

          {submissions.length > 0 ? (
            <>
              <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-gray-50 border-b border-gray-200 flex gap-2 xl:gap-4 items-center p-3 xl:p-4">
                <div className="flex-[2] min-w-0 text-[14px] font-bold text-[#33333b]">宿題</div>
                <div className="w-[72px] xl:w-[80px] shrink-0 text-[14px] font-bold text-[#33333b] whitespace-nowrap">ステータス</div>
                <div className="w-[80px] shrink-0 text-[14px] font-bold text-[#33333b]">区分</div>
                <div className="w-[40px] shrink-0 text-[14px] font-bold text-[#33333b] text-center">評価</div>
                <div className="flex-[3] min-w-0 text-[14px] font-bold text-[#33333b]">フィードバック</div>
              </div>

              <div className="divide-y divide-gray-200">
                {submissions.map(sub => {
                  const type = sub.homework?.type ?? 'system'
                  const color = getTypeColor(type)
                  const graded = isFullyGraded(sub)
                  const feedbackLines = sub.feedback_text
                    ? sub.feedback_text.split('\n').filter(l => l.trim())
                    : []

                  return (
                    <div key={sub.id} className="flex gap-2 xl:gap-4 items-center p-3 xl:p-4">
                      <div className="flex-[2] min-w-0 flex items-center">
                        <Link
                          to={`/homework/${sub.homework_id}`}
                          className="flex-1 text-[14px] underline break-words"
                          style={{ color }}
                        >
                          {sub.homework?.title}
                        </Link>
                      </div>

                      <div className="w-[72px] xl:w-[80px] shrink-0 flex items-center">
                        <span className="text-[14px] text-[#33333b] whitespace-nowrap">
                          {graded ? '完了' : '採点中'}
                        </span>
                      </div>

                      <div className="w-[80px] shrink-0 flex items-center">
                        <span
                          className="text-[12px] font-bold text-white px-[8px] py-[4px] rounded-full whitespace-nowrap"
                          style={{ backgroundColor: color }}
                        >
                          {type === 'school' ? '学校' : type === 'entre' ? 'アントレ部' : (sub.homework?.category ?? '')}
                        </span>
                      </div>

                      <div className="w-[40px] shrink-0 flex items-center justify-center">
                        {graded && (
                          <span className="text-[24px] leading-none text-center" style={{ color }}>
                            {sub.grade ?? '-'}
                          </span>
                        )}
                      </div>

                      <div className="flex-[3] min-w-0">
                        {graded && feedbackLines.length > 0 && (
                          <ul className="text-[14px] text-[#33333b]">
                            {feedbackLines.map((line, i) => (
                              <li key={i} className="leading-[1.4]">{line}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
              </div>
            </>
          ) : null}
        </div>

        {/* Right: stats card — shown even with 0 submissions so the 0件 tile is visible */}
        <div className="w-full lg:w-[300px] shrink-0">
          <div className="bg-white border border-gray-200 rounded-[8px] px-6 py-5 flex flex-col gap-5 items-center">

            {/* 提出済みの宿題（システム・学校・アントレ部すべて） */}
            <div className="w-full flex flex-col gap-[8px]">
              <p className="text-[14px] font-bold text-[#33333b]">提出済みの宿題数</p>
              <div className="bg-[#fff0f4] rounded-[4px] p-[16px] flex items-baseline justify-center gap-[4px] text-[#da4574]">
                <span className="text-[48px] leading-none">{submissions.length}</span>
                <span className="text-[24px] leading-none">件</span>
              </div>
            </div>

            {/* 現在設定中の宿題 */}
            {selectedHomework && (
              <div className="w-full flex flex-col gap-[8px]">
                <p className="text-[14px] font-bold text-[#33333b]">現在設定中の宿題</p>
                <div className="shadow-[0_0_4px_rgba(0,0,0,0.16)] rounded-[8px] overflow-hidden">
                  <div className="bg-white px-[32px] py-[16px] flex flex-col gap-[8px] items-center">
                    <span className="bg-[#65c1c8] text-white text-[14px] font-bold px-[24px] py-[4px] rounded-full whitespace-nowrap">
                      {selectedHomework.category ?? 'キャリア'}
                    </span>
                    <div className="w-full flex flex-col gap-[16px] items-center">
                      <div className="w-full flex flex-col gap-[8px]">
                        <p className="text-[20px] font-bold text-[#33333b] text-center leading-[1.4] overflow-hidden" style={{ maxHeight: '56px' }}>
                          {selectedHomework.title}
                        </p>
                        {selectedHomework.description && (
                          <p className="text-[14px] text-[#33333b] leading-[1.4] overflow-hidden w-full" style={{ display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical' }}>
                            {selectedHomework.description}
                          </p>
                        )}
                      </div>
                      <div className="bg-[#e9f9fa] rounded-full px-[16px] py-[8px] flex items-center justify-center w-full">
                        <div className="flex items-center gap-[4px]">
                          <span className="text-[14px] font-bold text-[#72727c]">締切</span>
                          <svg className="w-[14px] h-[14px] text-[#72727c]" viewBox="0 0 448 512" fill="currentColor">
                            <path d="M96 32V64H48C21.5 64 0 85.5 0 112v48H448V112c0-26.5-21.5-48-48-48H352V32c0-17.7-14.3-32-32-32s-32 14.3-32 32V64H160V32c0-17.7-14.3-32-32-32S96 14.3 96 32zM448 192H0V464c0 26.5 21.5 48 48 48H400c26.5 0 48-21.5 48-48V192z" />
                          </svg>
                          <span className="text-[14px] font-bold text-[#72727c]">
                            {formatDeadline(selectedHomework.deadline_at)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ミラッソを変更 */}
            {totalSystemCount > 0 && (
              <button
                onClick={() => navigate('/homework/system')}
                className="border border-[#e6e0e0] rounded-[16px] h-[32px] px-[16px] flex items-center gap-[4px]"
              >
                <span className="text-[14px] font-bold text-[#33333b]">ミラパスからの宿題を設定</span>
                <svg className="w-[14px] h-[14px] text-[#33333b]" viewBox="0 0 448 512" fill="currentColor">
                  <path d="M438.6 278.6c12.5-12.5 12.5-32.8 0-45.3l-160-160c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L338.8 224 32 224c-17.7 0-32 14.3-32 32s14.3 32 32 32l306.7 0L233.4 393.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0l160-160z" />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default HomeworkPage
